import { useCallback, useState } from 'react'
import type { FileRef } from '@/api/files'

/** A message-composer's editable state. */
export interface ComposerDraft {
  channel: string
  templateId: string
  subject: string
  body: string
  attachments: FileRef[]
}

const PREFIX = 'composer-draft:'

function emptyDraft(): ComposerDraft {
  return { channel: 'email', templateId: '', subject: '', body: '', attachments: [] }
}

function load(key: string): ComposerDraft {
  try {
    const raw = sessionStorage.getItem(PREFIX + key)
    if (raw) {
      return { ...emptyDraft(), ...(JSON.parse(raw) as Partial<ComposerDraft>) }
    }
  } catch {
    // malformed or unavailable storage — fall back to an empty draft
  }
  return emptyDraft()
}

/**
 * A message-composer draft persisted in sessionStorage, keyed per entity (e.g. `deal:<id>`), so a
 * half-written email survives navigating away and back — and a tab refresh. The typical case: an
 * agent drafts an email, opens the linked contact to re-subscribe an opted-out address, returns to
 * the deal, and finishes sending. Callers should give each composer a React `key` on the entity id
 * so a different entity mounts a fresh draft (the key here isolates storage; the React key isolates
 * component state).
 *
 * Cleared on a successful send.
 */
export function useComposerDraft(key: string): {
  draft: ComposerDraft
  update: (patch: Partial<ComposerDraft>) => void
  clear: () => void
} {
  const [draft, setDraft] = useState<ComposerDraft>(() => load(key))

  const update = useCallback(
    (patch: Partial<ComposerDraft>) => {
      setDraft((prev) => {
        const next = { ...prev, ...patch }
        try {
          sessionStorage.setItem(PREFIX + key, JSON.stringify(next))
        } catch {
          // storage full/unavailable — keep the in-memory draft anyway
        }
        return next
      })
    },
    [key],
  )

  const clear = useCallback(() => {
    try {
      sessionStorage.removeItem(PREFIX + key)
    } catch {
      // ignore
    }
    setDraft(emptyDraft())
  }, [key])

  return { draft, update, clear }
}
